import { Box, Button, CircularProgress, FormControl, Menu, MenuItem, Stack, TextField, Typography } from "@mui/material"
import { useCallback, useEffect, useRef, useState } from "react"
import { useQueryClient } from "react-query";

import config from "../common/Config";
import useFetch from "../api/api"
import useAuth from "../hooks/useAuth";
import useInternalFetch from "../api/apiInternal";

import SearchOffIcon from '@mui/icons-material/SearchOff';

const SearchStock = (props) => {

	const privateFetch							= useFetch()

	const [hasResults, setHasResults] 			= useState(false)
	const [searchResults, setSearchResults]		= useState([])
	const [anchorElement, setAnchorElement]		= useState(null)
	const [menuWidth, setMenuWidth]				= useState(0)
	const [selectedResult, setSelectedResult]	= useState()
	const [focused, setFocused]					= useState(false)

	useEffect(() => {
		if (hasResults) {
			document.addEventListener('keydown', keydownHandler);
		}
		else {	
			document.removeEventListener('keydown', keydownHandler);
			setFocused(false)
		}

		return () => {
			document.removeEventListener('keydown', keydownHandler);
			setFocused(false)
		}
	}, [hasResults])

	const keydownHandler = (e) => {
		if (e.key === 'ArrowDown') setFocused(true);
	}
 
	const handleSearch = (e) => {
		setAnchorElement(e.target)
		setMenuWidth(e.target.offsetWidth)

		const query = e.target.value

		if (query.length >= 3) {
			setHasResults(true)

			privateFetch(config.apiUrl +'/api/companies/search?query='+ query)
			.then((data) => data.json())
			.then((data) => {
				setSearchResults(data)
			})
		}		
	}

	const handleClose = () => {
		setHasResults(false)
	}

	const handleClick = (e) => {
		setSelectedResult(e)
		setHasResults(false)
		if (props.onClick) props.onClick(e)
	}

	const handleResetSearch = () => {
		setSelectedResult(null)
	}

	return (
		<FormControl sx={{width: props.width ? props.width : '100%', justifyContent: 'center', alignItems: 'center'}} >

			{ selectedResult ? ( 
				<Stack direction={'row'} height={'100%'} width={'100%'} onClick={handleResetSearch} alignItems={'center'} border={1} borderRadius={'4px'} borderColor={'lightgray'}>
					<Box display={'flex'} sx={{backgroundColor: 'lightGray', marginLeft: 1, width: '30px', height:'30px', borderRadius: '25px'}} justifyContent={'center'} alignItems={'center'}>
						<img
							loading="lazy"
							width="20"
							src={selectedResult.imageUrl}
							srcSet={selectedResult.imageUrl}
							alt=""
							/>
					</Box>
					<Typography sx={{ml:1}}>{selectedResult.ticker} - {selectedResult.name}</Typography>
				</Stack>
			) : (
				<TextField 
					fullWidth 
					{...props.usePlaceHolder ? {placeholder:props.label} : {label: (props.label ? props.label : "Ticker")}}
					onChange={handleSearch}
					sx={props.sx}
					error={props.error}
					helperText={props.helperText}
				/>
			)}
			<Menu
				open={hasResults}
				anchorEl={anchorElement}
				onClose={handleClose}
				autoFocus={focused} 
				disableAutoFocusItem={!focused}
				disableAutoFocus={!focused}
				sx={{maxHeight:400}}
				>

				{ searchResults.length === 0 ? (
					<MenuItem sx={{width: menuWidth}}>
						<SearchOffIcon/>
						<Typography sx={{ml:1}}>No company found</Typography>
					</MenuItem>
				) : null }

				{ searchResults.map(result => <SearchStockResult result={result} onClick={() => handleClick(result)}/>) }

			</Menu>
		</FormControl>
	)
}

const SearchStockResult = (props) => {

	const { auth } 								= useAuth()
	const privateFetch							= useInternalFetch()

	const imgRef 								= useRef()
	
	const [result, setResults]					= useState(props.result || [])
	const [isSaving, setIsSaving]				= useState(false)
	
	const queryClient							= useQueryClient()

	useEffect(() => setResults(props.result)
	, [props.result])

	const [backGround, setBackGround] 			= useState([0,0,0])

	const readImageData = useCallback(() => {
		const img = imgRef.current;

		var rgb = {r:0,g:0,b:0},
			i = -4,
			blockSize = 5,
			count = 0,
			length

		if (!img?.width) return;

		const { width, height }	= img;
		const canvas 			= document.createElement("canvas");
		canvas.height 			= height;
		canvas.width 			= width;
		const context 			= canvas.getContext?.("2d");

		if (context === null) return;

		context.drawImage(img, 0, 0);
		const imageData 		= context.getImageData(0, 0, width, height);	
		length 					= imageData.data.length;
	
		while ( (i += blockSize * 4) < length ) {
			++count;
			rgb.r += imageData.data[i];
			rgb.g += imageData.data[i+1];
			rgb.b += imageData.data[i+2];
		}
	
		// ~~ used to floor values
		rgb.r = ~~(rgb.r/count);
		rgb.g = ~~(rgb.g/count);
		rgb.b = ~~(rgb.b/count);
		
		setBackGround(rgb.r + rgb.g + rgb.b === 0 ? [211,211,211] : [rgb.r,rgb.g,rgb.b])
	}, []);

	const handleAddToPortfolio = (pTicker, pAssetType) => {
		setIsSaving(true)

		let options = {
			method : "POST",
			headers : {
				"Content-Type" : "application/json"	
			},
			body : `{ 
				"ticker" 	: "`+ pTicker +`", 
				"assetType"	: "`+ pAssetType + `",
				"target" 	: "0"
			}`			
		}
		
		privateFetch(config.apiUrl +"/api/portfolio/addTarget", options)
		.then(() => {
			// Atualiza o array local para dizer que está no portfolio (trocar para icone de remover)
			setResults(prev => 
				Array.isArray(prev)
					? prev.map(item => item.ticker === pTicker ? { ...item, inPortfolio: true } : item )
					: { ...prev, inPortfolio: true }
			)

			// Atualiza a listagem de portfolio
			queryClient.invalidateQueries(['portfolio_'+ pAssetType])
		})
		.finally(() => setIsSaving(false))

	}

	const handleRemoveFromPortfolio = (pTicker, pAssetType) => {
		setIsSaving(true)

		let options = {
			method : "DELETE",
			headers : {
				"Content-Type" : "application/json"	
			}
		}

		privateFetch(config.apiUrl +"/api/portfolio/"+ pTicker, options)
		.then(() => {
			// Atualiza o array local para dizer que está no portfolio (trocar para icone de remover)
			setResults(prev => 
				Array.isArray(prev)
					? prev.map(item => item.ticker === pTicker ? { ...item, inPortfolio: true } : item )
					: { ...prev, inPortfolio: false }
			)

			// Atualiza a listagem de portfolio
			queryClient.invalidateQueries(['portfolio_'+ pAssetType])
		})
		.finally(() => setIsSaving(false))
	}	

	return (
		<MenuItem key={result.ticker} sx={{width: props.menuWidth || 600}}>
			<Box flex={1} display={'flex'} alignItems={'center'} onClick={props.onClick}>
				<Box 
					display='flex' 
					sx={{
						backgroundColor:'rgb('+ backGround[0]+','+ backGround[1]+','+ backGround[2]+')', 
						width: '30px', 
						height:'30px', 
						borderRadius: '50%'
					}} 
					justifyContent={'center'} 
					alignItems={'center'}
					>
					<img
						ref={imgRef}
						loading="lazy"
						crossOrigin="anonymous"
						width="20"
						src={result.imageUrl}
						srcSet={result.imageUrl}
						alt=""
						onLoad={readImageData}
					/>
				</Box>
				<Typography sx={{ml:1, flex: 1}} noWrap>{result.ticker} - {result.name}</Typography>
			</Box>
			{auth.userId ? result.inPortfolio ? (
				<Button 
					variant="contained"
					color="secondary" 
					sx={{borderRadius: 25, width: 25}} 
					onClick={() => handleRemoveFromPortfolio(result.ticker, result.assetType)}
					disabled={isSaving}
					>
					{isSaving ? <CircularProgress size={21}/> : "-"}
				</Button>
			) : (
				<Button 
					variant="contained" 
					sx={{borderRadius: 25}} 
					onClick={() => handleAddToPortfolio(result.ticker, result.assetType)}
					disabled={isSaving}
					>
					{isSaving ? <CircularProgress size={21}/> : "+"}
				</Button>
			) : null}
			
		</MenuItem>
	)
}



export default SearchStock 